import React from 'react';
import { Box, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// ==============================|| 記事本文のインライン記法 ||============================== //
//
// 記事データ(src/const/blog/articles/*.ts)の文字列で使える記法を解釈する。
// 対応するのは次の2つだけで、これ以上は増やさない方針。
//
//   **強調**            -> 太字
//   [表示文字](/path)   -> リンク。'/' 始まりはサイト内、それ以外(http〜)は別タブの外部リンク
//
// Markdown のパーサーを入れなかったのは、記法を増やすと記事ごとに書きぶりが
// ばらついてデザインが崩れるため。見出し・箇条書き・表などの「構造」は
// 記事データ側でブロックとして持たせており(types/blog.ts)、
// ここが受け持つのは1行の中の装飾だけに限定している。

const primaryColor = '#2aa1af';

// **強調** と [表示文字](リンク先) を、文中から順番に拾うための正規表現。
// 2つをまとめて1本にしているのは、出現順どおりに処理するため。
const INLINE_PATTERN = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;

const linkSx = {
    color: primaryColor,
    fontWeight: 700,
    textDecorationColor: 'rgba(42, 161, 175, 0.4)'
};

/**
 * 記事本文の1行を React の要素列に変換する。
 *
 * 戻り値を配列ではなくフラグメントで返しているのは、呼び出し側が
 * <Typography>{inline(text)}</Typography> のようにそのまま流し込めるようにするため。
 */
export const inline = (text: string): React.ReactNode => {
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // exec を繰り返すため lastIndex を持つ。正規表現は使い回すと状態が残るのでここで初期化する
    INLINE_PATTERN.lastIndex = 0;

    while ((match = INLINE_PATTERN.exec(text)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(text.slice(lastIndex, match.index));
        }

        const [, strong, linkLabel, href] = match;

        if (strong) {
            nodes.push(
                <Box key={nodes.length} component="strong" sx={{ fontWeight: 700, color: '#111827' }}>
                    {strong}
                </Box>
            );
        } else if (linkLabel && href) {
            nodes.push(
                href.startsWith('/') ? (
                    <Link key={nodes.length} component={RouterLink} to={href} underline="hover" sx={linkSx}>
                        {linkLabel}
                    </Link>
                ) : (
                    <Link key={nodes.length} href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={linkSx}>
                        {linkLabel}
                    </Link>
                )
            );
        }

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        nodes.push(text.slice(lastIndex));
    }

    return <>{nodes}</>;
};

/**
 * 記法を取り除いた素の文字列を返す。
 * meta description や構造化データ(FAQPage の回答文など)に使う。
 * これらは検索エンジンがそのまま読むため、'**' などの記号を残してはいけない。
 */
export const stripInline = (text: string): string =>
    text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
